import * as React from 'react';
import type { ValidIconName } from './Icon';
import { Icon } from './Icon';
import type { ScaleProp, VoidCallback } from '../common-types';
import { Button } from './Button';
import { Link } from './Link';

/**
 * Common properties for a {@link BlankState} component.
 */
interface CommonBlankStateProps extends ScaleProp {
  iconName: ValidIconName;
  description: string | JSX.Element;
  buttonText?: string;
}

/**
 * Properties for a {@link BlankState} component that also contains a {@link Button}.
 */
export interface BlankStateButtonProps extends CommonBlankStateProps {
  onClick?: VoidCallback;
  href?: never;
}

/**
 * Properties for a {@link BlankState} component that also contains a {@link Link}.
 */
export interface BlankStateLinkProps extends CommonBlankStateProps {
  href?: string;
  onClick?: never;
}

/**
 * Props for a {@link BlankState} component.
 * BlankState can only contain either onClick or href properties, but not both.
 */
export type BlankStateProps = BlankStateButtonProps | BlankStateLinkProps;

export function BlankState({
  iconName,
  description,
  scale = 'small',
  buttonText,
  onClick,
  href,
  ...props
}: Readonly<BlankStateProps>): JSX.Element {
  return (
    <div {...props} className={'ehs-blank-state blank-state-' + scale}>
      <Icon
        aria-label={undefined}
        className="blank-state-icon"
        iconName={iconName}
      />
      <span className="blank-state-description">{description}</span>
      {buttonText && (onClick || href) && (
        <span className="blank-state-button">
          {onClick && (
            <Button
              variant="gray"
              text={buttonText}
              onClick={onClick}
              scale={scale}
            />
          )}
          {href && (
            <Link variant="gray" href={href} scale={scale}>
              {buttonText}
            </Link>
          )}
        </span>
      )}
    </div>
  );
}
